import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, Container, Modal, ModalBody, ModalFooter, ModalHeader, Button, Col, Row, Label, Input, FormGroup } from "reactstrap";

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";


// API helper and URL helper
import { get, del, put } from "../../helpers/api_helper";
import { NOZZLE_LIST, UPDATE_NOZZLE, DELETE_NOZZLE } from "../../helpers/url_helper";

// Toaster
import toastr from "toastr";
import "toastr/build/toastr.min.css";

// React Router
import { Link, useNavigate } from "react-router-dom";
import nozzleColumns from "components/Columns/NozzleColumns";
import { Controller, useForm } from "react-hook-form";



const Nozzles = () => {
  const [nozzles, setNozzles] = useState([]);

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNozzle, setSelectedNozzle] = useState(null);
  const [nozzleToEdit, setNozzleToEdit] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const navigate = useNavigate();
  const toggleEditModal = () => setEditModalOpen(!editModalOpen);
  const updateNozzle = (id, data) => put(UPDATE_NOZZLE(id), data);
  const [isAdmin, setIsAdmin] = useState(false);
 
  useEffect(() => {
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const role = Number(authUser?.user?.role);
      if (role !== 1) {
        navigate("/dashboard"); // 🔁 Redirect to dashboard if not admin
      } else {
        setIsAdmin(true);
      }
    } catch {
      navigate("/dashboard"); // 🔁 Redirect to dashboard if error
    }
  }, [navigate]);
  const breadcrumbItems = [
    { title: "valfuel", link: "/" },
    { title: "Nozzles", link: "#" },
  ];

  

  async function getNozzleList(page, size, sort = [], search = "") {
    const sortKey = sort[0]?.id;
    const sortby = sort[0]?.desc ? "desc" : "asc";

    return await get(NOZZLE_LIST, {
      params: { page, size, sortKey, sortby },
    });
  }


  const { register, handleSubmit, control, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      nozzel_no: "",
      vehicle_no: "",
      total_capacity: "",
      remaining_capacity: "",
      status: 0,
      notes: "",
    }
  });

  useEffect(() => {
    if (nozzleToEdit) {
      reset({
        nozzel_no: nozzleToEdit.nozzel_no || "",
        vehicle_no: nozzleToEdit.vehicle_no || "",
        total_capacity: nozzleToEdit.total_capacity || "",
        remaining_capacity: nozzleToEdit.remaining_capacity || "",
        status: nozzleToEdit.status ?? 0,
        notes: nozzleToEdit.notes || "",
      });
    }
  }, [nozzleToEdit]);

  const fetchNozzles = async (page = 1, size = 10, sortBy = [], searchText = '') => {
    try {
      setLoading(true);
      const response = await getNozzleList(page, size, sortBy, searchText);
      const { items, totalPages } = response.data;

      setNozzles(items);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Failed to fetch nozzles:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (classItem) => {
    setNozzleToEdit(classItem);
    setEditModalOpen(true);
  };

  const handleDelete = (item) => {
    setSelectedNozzle(item);
    setModalOpen(true);
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const confirmDelete = async () => {
    try {
      if (selectedNozzle) {
        await del(DELETE_NOZZLE(selectedNozzle.ul_id)); // Call delete API
        toastr.success("Nozzle deleted successfully!");
        fetchNozzles(pageIndex, pageSize, sortBy, searchText);
      }
    } catch (error) {
      console.error("Failed to delete nozzle:", error?.response?.data || error?.message);
      toastr.error("Something went wrong while deleting.");
    }
    setModalOpen(false);
  };

  const onSubmitEdit = async (data) => {
    try {
      await updateNozzle(nozzleToEdit.ul_id, data);
      toastr.success("Nozzle updated successfully!");
      fetchNozzles();
      toggleEditModal();
    } catch (error) {
      toastr.error("Failed to update nozzle.");
      console.error(error);
    }
  };

  const handleSearchChange = (text) => {
    setPageIndex(1);
    setSearchText(text);
  };

  const didFetchRef = useRef(false);
  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;

    fetchNozzles(pageIndex, pageSize, sortBy, searchText);
  }, []);


  const columns = useMemo(
    () => nozzleColumns(handleEdit, handleDelete, isAdmin),
    [handleEdit, handleDelete, isAdmin]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Nozzles" breadcrumbItems={breadcrumbItems} />
        </Container>

        <Card>
          <CardBody>
            {loading ? (
              <div className="text-center">Loading...</div>
            ) : nozzles.length > 0 ? (
              <TableContainer
                columns={columns || []}
                data={nozzles || []}
                isPagination={true}
                isGlobalFilter={true}
                customPageSize={pageSize}
                isAddOptions={isAdmin}
                addOptionTitle={"Add New Nozzle"}
                iscustomPageSize={false}
                isBordered={false}
                controlledPagination={{ pageIndex, setPageIndex, pageSize, setPageSize, totalPages }}
                onSortChange={setSortBy}
                searchText={searchText}
                onSearchChange={handleSearchChange}
                handleAddClick={isAdmin ? () => navigate("/add-nozzle") : undefined}
              />
            ) : (
              <div className="text-center py-5">
                <h5>No Record Found</h5>
                {isAdmin && (
                  <Link to="/add-nozzle" className="btn btn-primary mt-3">
                    Add New Nozzle
                  </Link>
                )}

              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          Confirm Deletion
        </ModalHeader>
        <ModalBody>Are you sure you want to delete this nozzle permanently?</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDelete}>
            Yes, Delete
          </Button>
        </ModalFooter>
      </Modal>
      <Modal size="lg" isOpen={editModalOpen} toggle={toggleEditModal} >
        <ModalHeader toggle={toggleEditModal}>Edit Nozzle</ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit(onSubmitEdit)}>
            <Row>
              <Col md="12" xl="6">
                <FormGroup className="mb-3">
                  <Label>Nozzle No <span className="text-danger">*</span></Label>
                  <input
                    type="text"
                    className={`form-control ${errors.nozzel_no ? "is-invalid" : ""}`}
                    {...register("nozzel_no", { required: "Nozzle number is required" })}
                  />
                  {errors.nozzel_no && <div className="text-danger">{errors.nozzel_no.message}</div>}
                </FormGroup>
              </Col>

              <Col md="12" xl="6">
                <FormGroup className="mb-3">
                  <Label>Vehicle Number <span className="text-danger">*</span></Label>
                  <Controller
                    name="vehicle_no"
                    control={control}
                    rules={{
                      required: "Vehicle Number is required",
                      pattern: {
                        value: /^[A-Z]{2}-\d{1,2}-[A-Z]{1,3}-\d{4}$/,
                        message: "Format must be like GJ-19-Z-1587",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        maxLength={15}
                        onChange={(e) => {
                          let raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');

                          // Extract parts strictly based on format
                          const state = raw.slice(0, 2);
                          const district = raw.slice(2, 4); // can be 1 or 2 digits
                          let remaining = raw.slice(4);

                          let series = '';
                          let number = '';

                          // Extract series (letters) and number (digits) from the rest
                          const match = remaining.match(/^([A-Z]{1,3})(\d{1,4})?$/);
                          if (match) {
                            series = match[1] || '';
                            number = match[2] || '';
                          }

                          // Combine into formatted string
                          let formatted = [state, district, series, number]
                            .filter(Boolean)
                            .join('-');

                          field.onChange(formatted);
                        }}
                        className={`form-control ${errors.vehicle_no ? 'is-invalid' : ''}`}
                        placeholder="e.g. GJ-00-AA-0000"
                      />
                    )}
                  />


                  {errors.vehicle_no && <div className="text-danger">{errors.vehicle_no.message}</div>}
                </FormGroup>
              </Col>

              <Col md="12" xl="6">
                <FormGroup className="mb-3">
                  <Label>Total Capacity <span className="text-danger">*</span></Label>
                  <Controller
                    name="total_capacity"
                    control={control}
                     rules={{
                      required: "Total capacity is required",
                      pattern: {
                        value: /^\d{1,6}(\.\d{0,2})?$/,
                        message: "Only numbers and one dot allowed (max 2 decimals)",
                      },
                      maxLength: {
                        value: 9,
                        message: "Maximum 8 characters allowed",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <input
                          type="text"
                          className={`form-control ${error ? "is-invalid" : ""}`}
                          inputMode="decimal"
                          pattern="[0-9.]*"
                          maxLength={8}
                          {...field}
                        />
                        {errors.total_capacity && <div className="text-danger">{errors.total_capacity.message}</div>}
                      </>
                    )}
                  />
                </FormGroup>
              </Col>

              <Col md="12" xl="6">
                <FormGroup className="mb-3">
                  <Label>Remaining Capacity <span className="text-danger">*</span></Label>
                  <Controller
                    name="remaining_capacity"
                    control={control}
                    rules={{
                      required: "Remaining capacity is required",
                      pattern: {
                        value: /^\d{1,6}(\.\d{0,2})?$/,
                        message: "Only numbers and one dot allowed (max 2 decimals)",
                      },
                      maxLength: {
                        value: 9,
                        message: "Maximum 9 characters allowed",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <input
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9.]*"
                          maxLength={8}
                          className={`form-control ${error ? "is-invalid" : ""}`}
                          onChange={(e) => {
                            // Allow only digits and a single dot
                            const value = e.target.value;
                            if (/^[0-9]*\.?[0-9]*$/.test(value)) {
                              field.onChange(value);
                            }
                          }}
                          value={field.value}
                        />
                        {error && <div className="text-danger">{error.message}</div>}
                      </>
                    )}
                  />
                </FormGroup>
              </Col>

              <Col md="12" xl="6">
                <FormGroup className="mb-3">
                  <Label>Status</Label>
                  <select className="form-control" {...register("status")}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </FormGroup>
              </Col>

              <Col md="12" xl="6">
                <FormGroup className="mb-3">
                  <Label>Remarks</Label>
                  <textarea
                    rows="3"
                    className="form-control"
                    {...register("notes")}
                  />
                </FormGroup>
              </Col>
            </Row>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleEditModal}>Cancel</Button>
          <Button color="primary" onClick={handleSubmit(onSubmitEdit)}>Save Changes</Button>
        </ModalFooter>
      </Modal>


    </React.Fragment>
  );
};

export default Nozzles;
